import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axios";
import EntityManagement from "../formTemplates/EntityManagement";
import EntityTable from "../templateComponents/EntityTable";
import useDropdownPostFetch from "../apiFetch/useDropdownPostFetch";
import { entitiesConfig } from "../templateComponents/constants";

const BeaconRegistration = () => {
  const entityConfig = entitiesConfig.beacons;

  const [formData, setFormData] = useState(() => {
    const initialData = {};
    entityConfig.formFields.forEach((field) => {
      initialData[field.name] = "";
    });
    return initialData;
  });

  const [entities, setEntities] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const firstFormFieldRef = useRef(null);

  const beaconTypeOptions = useDropdownPostFetch(entityConfig.dropdowns.beacon_type, 
    { view_type: "view" });

  const fetchEntities = async () => {
    try {
      const response = await axiosInstance.post(entityConfig.endpoints.fetch, {
        view_type: "view all",
      });
      if (response.data.Status === "Success") {
        setEntities(response.data.Data || []);
      } else {
        console.error("Fetch error:", response.data.Message);
      }
    } catch (err) {
      console.error("Error loading beacons:", err);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.bec_name || !formData.bec_type_id) {
      alert("Please enter all required fields.");
      return;
    }

    const payload = {
      bec_name: formData.bec_name.trim(),
      bec_type_id: parseInt(formData.bec_type_id, 10),
    };

    if (editMode) {
      payload.bec_id = editingEntityId;
      try {
        const response = await axiosInstance.post(entityConfig.endpoints.update, payload);
        if (response.data.Status === "Success") {
          await fetchEntities();
          resetForm();
        } else {
          console.error("Update failed:", response.data.Message);
        }
      } catch (err) {
        console.error("Error updating:", err);
      }
    } else {
      try {
        const response = await axiosInstance.post(entityConfig.endpoints.create, payload);
        if (response.data.Status === "Success") {
          await fetchEntities();
          resetForm();
        } else {
          console.error("Registration failed:", response.data.Message);
        }
      } catch (err) {
        console.error("Error registering:", err);
      }
    }
  };

  const resetForm = () => {
    const initialData = {};
    entityConfig.formFields.forEach((field) => {
      initialData[field.name] = "";
    });
    setFormData(initialData);
    setEditMode(false);
    setEditingEntityId(null);
  };

  const handleEdit = (entityId) => {
    const entity = entities.find((e) => e.bec_id === entityId);
    if (entity) {
      const updatedFormData = {};
      entityConfig.formFields.forEach((field) => {
        updatedFormData[field.name] = entity[field.name]?.toString() || "";
      });
      setFormData(updatedFormData);
      setEditMode(true);
      setEditingEntityId(entityId);
      if (firstFormFieldRef.current) firstFormFieldRef.current.focus();
      setIsTableModalOpen(false);
    }
  };

  return (
    <div className="beacon-master">
      <EntityManagement
        entityName={entityConfig.name}
        formFields={entityConfig.formFields}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
        dropdownData={{
          beacon_type: beaconTypeOptions.options || [],
        }}
        firstFormFieldRef={firstFormFieldRef}
        handleViewTableClick={() => setIsTableModalOpen(true)}
        onEdit={handleEdit}
      />

      {isTableModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <button onClick={() => setIsTableModalOpen(false)}>Close</button>
            <EntityTable
              data={entities.map((entity) => ({
                ...entity,
                bec_type_name:
                  beaconTypeOptions.options.find((option) => option.value === entity.bec_type_id)
                    ?.label || entity.bec_type_id,
              }))}
              columns={entityConfig.columns}
              onEdit={handleEdit}
              keyField="bec_id"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BeaconRegistration;
